import { useContext, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Container,
  Dialog,
  DialogContent,
  IconButton,
  Snackbar,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useMutation, useQuery } from "@apollo/client";
import Footer from "../../components/Footer/Footer";
import { Header } from "../../components/Header";
import UserContext from "../../context/User";
import { useLocation } from "react-router-dom";
import ApplyButton from "../../components/ApplyButton/ApplyButton";
import LabelTextField from "../../components/LabelTextFIeld/LabelTextField";
import Dropdown from "../../components/DropDown/DropDown";
import Message from "../../components/Message/Message";
import TicketChatModal from "../../components/Support/TicketChatModal";
import {
  CREATE_SUPPORT_TICKET,
  GET_SINGLE_SUPPORT_TICKET,
  GET_USER_SUPPORT_TICKETS,
} from "./GetHelp.queries";
import useStyles from "./styles";
import FavouritesBg from "../../assets/images/fav-bg.png";

function GetHelp() {
  const classes = useStyles();
  const location = useLocation();
  const { profile } = useContext(UserContext);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const [formData, setFormData] = useState({
    reason: "",
    orderId: "",
    title: "",
    description: "",
  });

  const userId = profile?._id;
  const userEmail = profile?.email || "-";
  const userName = profile?.name || "";
  const faqs = [
    {
      question: "Can I track my order?",
      answer:
        "Yes, you can track your order in real-time from the moment it's confirmed to when it's out for delivery. You'll receive updates on the status of your order through notifications on the app.",
    },
    {
      question: "How long does delivery take?",
      answer:
        "Delivery times may vary depending on factors such as the restaurant's preparation time, traffic conditions, and your location. However, we strive to deliver your order within a reasonable timeframe, typically ranging from 30 to 60 minutes.",
    },
    {
      question: "Can I cancel my order after it's been placed?",
      answer:
        "Yes, you can cancel your order once it is placed. However, once the order has been prepared and sent out for delivery, you can no longer cancel the order. You may contact the restaurant directly for assistance.",
    },
    {
      question: "How can I provide feedback on my experience?",
      answer:
        "You can provide feedback through the app by rating your order and leaving comments. You can also give a rating to the restaurant and leave a review.",
    },
    {
      question: "Can I order from multiple places at the same time?",
      answer:
        "Unfortunately, you can't choose from multiple restaurants within the same order. However, you can place separate orders from different restaurants at the same time.",
    },
  ];

  useEffect(() => {
    if (!isCreateModalOpen) return;
    const params = new URLSearchParams(location.search);
    const orderIdFromQuery = params.get("orderId");
    if (!orderIdFromQuery) return;
    setFormData((prev) => ({
      ...prev,
      reason: "Order related",
      orderId: orderIdFromQuery,
    }));
  }, [location.search, isCreateModalOpen]);

  const showAlert = (severity, message) => {
    setAlert({ open: true, severity, message });
  };

  const {
    data: ticketsData,
    loading: ticketsLoading,
    refetch: refetchTickets,
  } = useQuery(GET_USER_SUPPORT_TICKETS, {
    variables: {
      input: {
        userId: userId,
        filters: { page: 1, limit: 10 },
      },
    },
    skip: !userId,
    fetchPolicy: "network-only",
    onError: (error) => {
      showAlert("error", error.message || "Failed to fetch tickets");
    },
  });

  const [createSupportTicket] = useMutation(CREATE_SUPPORT_TICKET, {
    onCompleted: () => {
      setIsSubmitting(false);
      showAlert("success", "Support ticket created successfully.");
      handleCloseCreateModal();
      refetchTickets();
    },
    onError: (error) => {
      setIsSubmitting(false);
      showAlert("error", error.message || "Something went wrong");
    },
  });

  const tickets = useMemo(() => {
    return ticketsData?.getSingleUserSupportTickets?.tickets || [];
  }, [ticketsData]);
  const reasonLower = formData.reason.trim().toLowerCase();
  const isOrderRelated = reasonLower === "order related";
  const isOtherReason = reasonLower === "others";
  const trimmedOrderId = formData.orderId.trim();
  const trimmedTitle = formData.title.trim();
  const trimmedDescription = formData.description.trim();

  const sortedTickets = useMemo(() => {
    return [...tickets].sort((a, b) => {
      const dateA = new Date(parseInt(a.updatedAt, 10)).getTime();
      const dateB = new Date(parseInt(b.updatedAt, 10)).getTime();
      return dateB - dateA;
    });
  }, [tickets]);

  const handleFieldChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleOpenCreateModal = () => setIsCreateModalOpen(true);

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setFormData({ reason: "", orderId: "", title: "", description: "" });
  };

  const handleCreateTicket = () => {
    if (!reasonLower)
      return showAlert("error", "Please select a reason for your inquiry.");
    if (isOrderRelated && !trimmedOrderId)
      return showAlert("error", "Please provide your order ID.");
    if (isOtherReason && !trimmedTitle)
      return showAlert("error", "Please provide a title for your inquiry.");
    if (!trimmedDescription)
      return showAlert("error", "Please provide a description of the issue.");

    const finalTitle = isOrderRelated
      ? `Order Issue - ${trimmedOrderId}`
      : trimmedTitle;

    const finalDescription = isOrderRelated
      ? `Order ID: ${trimmedOrderId}\n\n${trimmedDescription}`
      : trimmedDescription;

    const ticketInput = {
      title: finalTitle,
      description: finalDescription,
      category: reasonLower,
      userType: "User",
    };

    if (isOrderRelated) {
      ticketInput.orderId = trimmedOrderId;
    } else {
      ticketInput.otherDetails = trimmedTitle;
    }

    setIsSubmitting(true);
    createSupportTicket({
      variables: { ticketInput },
    });
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(parseInt(dateString, 10));
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "unknown date";
    }
  };

  const getStatusClass = (status) => {
    const normalized = (status || "").toLowerCase();
    if (normalized === "open") return classes.statusOpen;
    if (normalized === "inprogress" || normalized === "in progress") {
      return classes.statusInProgress;
    }
    if (normalized === "closed") return classes.statusClosed;
    return classes.statusClosed;
  };

  const formatStatus = (status) => {
    if (!status) return "";
    if (status.toLowerCase() === "inprogress") return "In Progress";
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const handleOpenChat = (ticketId) => {
    setSelectedTicket(ticketId);
    setIsChatOpen(true);
  };

  const handleCloseChat = () => {
    setIsChatOpen(false);
    refetchTickets();
  };

  const handleCloseAlert = (event, reason) => {
    if (reason === "clickaway") return;
    setAlert((prev) => ({ ...prev, open: false }));
  };
  const handleFaqChange = (panel) => (_, isExpanded) => {
    setExpandedFaq(isExpanded ? panel : null);
  };

  return (
    <Box className={classes.root}>
      <Header />
      <Box className={classes.topContainer}>
        <img src={FavouritesBg} alt="fav-bg" className={classes.bg} />
      </Box>
      <Container className={classes.mainContainer} maxWidth="md">
        <Box className={classes.section}>
          <Box className={classes.card}>
            <Typography className={classes.sectionTitle}>
              How can we help?
            </Typography>
            <Box className={classes.faqList}>
              {faqs.map((faq, index) => (
                <Accordion
                  key={faq.question}
                  expanded={expandedFaq === index}
                  onChange={handleFaqChange(index)}
                  className={classes.faqAccordion}
                  disableGutters
                  elevation={0}
                >
                  <AccordionSummary
                    expandIcon={<KeyboardArrowRightIcon className={classes.faqIcon} />}
                    className={classes.faqSummary}
                  >
                    <Typography className={classes.faqQuestion}>
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.faqDetails}>
                    <Typography className={classes.faqAnswer}>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
            <Typography className={classes.sectionTitle}>
              Still need help?
            </Typography>
            <Typography className={classes.sectionText}>
              Our support team is available to assist you with any questions or
              issues you may have.
            </Typography>
            <ApplyButton
              text="Chat with a person"
              onClick={handleOpenCreateModal}
              loading={false}
            />
          </Box>
        </Box>

        <Box className={classes.section}>
          <Box className={classes.card}>
            <Typography className={classes.sectionTitle}>
              Your Customer Support Tickets
            </Typography>
            {ticketsLoading ? (
              <Typography className={classes.helperText}>
                Loading tickets...
              </Typography>
            ) : sortedTickets.length > 0 ? (
              <Box className={classes.ticketList}>
                {sortedTickets.map((ticket) => (
                  <Box key={ticket._id} className={classes.ticketCard}>
                    <Box className={classes.ticketHeader}>
                      <Box>
                        <Typography className={classes.ticketTitle}>
                          {ticket.title}
                        </Typography>
                        <Typography className={classes.ticketMeta}>
                          Ticket ID {ticket._id}
                        </Typography>
                      </Box>
                      <span
                        className={`${classes.statusChip} ${getStatusClass(ticket.status)}`}
                      >
                        {formatStatus(ticket.status)}
                      </span>
                    </Box>
                    <Box className={classes.ticketDates}>
                      <Typography className={classes.ticketMeta}>
                        Created {formatDate(ticket.createdAt)}
                      </Typography>
                      <Typography className={classes.ticketMeta}>
                        Last Updated {formatDate(ticket.updatedAt)}
                      </Typography>
                    </Box>
                    <Box className={classes.ticketActions}>
                      <button
                        type="button"
                        className={classes.linkButton}
                        onClick={() => handleOpenChat(ticket._id)}
                      >
                        View Messages
                      </button>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box className={classes.emptyState}>
                <Typography className={classes.sectionText}>
                  You have no support tickets yet.
                </Typography>
                <Box className={classes.centerButton}>
                  <ApplyButton
                    text="Create your first ticket"
                    onClick={handleOpenCreateModal}
                    loading={false}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Container>

      <Box className={classes.footerContainer}>
        <Box className={classes.footerWrapper}>
          <Footer />
        </Box>
      </Box>

      <Dialog
        open={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent className={classes.modalContent}>
          <Box className={classes.modalHeader}>
            <Typography className={classes.modalTitle}>Support</Typography>
            <IconButton
              onClick={handleCloseCreateModal}
              className={classes.modalClose}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box className={classes.modalBody}>
            <Typography className={classes.modalLabel}>
              Hi {userName}
            </Typography>
            <Typography className={classes.modalSubText}>
              Email: {userEmail}
            </Typography>
            <Dropdown
              label="What's your issue about?"
              name="reason"
              placeholder="Select a reason"
              options={["Order related", "Others"]}
              value={formData.reason}
              onChange={handleFieldChange}
            />
            {isOrderRelated ? (
              <LabelTextField
                label="Order ID"
                name="orderId"
                placeholder="Enter order ID"
                value={formData.orderId}
                onChange={handleFieldChange}
              />
            ) : null}
            {isOtherReason ? (
              <LabelTextField
                label="Title"
                name="title"
                placeholder="Enter ticket title"
                value={formData.title}
                onChange={handleFieldChange}
              />
            ) : null}
            <Message
              label={
                formData.reason.toLowerCase() === "order related"
                  ? "Describe your order issue"
                  : "Tell us more about your issue"
              }
              name="description"
              placeholder="Please describe your issue in detail..."
              value={formData.description}
              onChange={handleFieldChange}
            />
            <Box className={classes.sendContainer}>
              <ApplyButton
                text="Send"
                onClick={handleCreateTicket}
                loading={isSubmitting}
              />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {selectedTicket && (
        <TicketChatModal
          visible={isChatOpen}
          onHide={handleCloseChat}
          ticketId={selectedTicket}
          ticketQuery={GET_SINGLE_SUPPORT_TICKET}
        />
      )}

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={alert.open}
        autoHideDuration={4000}
        onClose={handleCloseAlert}
      >
        <Alert
          severity={alert.severity}
          sx={{ width: "100%" }}
          onClose={handleCloseAlert}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default GetHelp;
